import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props { children: ReactNode; fallback?: ReactNode }
interface State { hasError: boolean; isChunkError: boolean }

/**
 * Captura erros ao carregar chunks lazy-loaded (comum quando o servidor
 * Vite invalida hashes de optimize-deps após HMR/restart, ou quando uma
 * nova versão é publicada e os arquivos antigos retornam 404/502).
 * Nesses casos, recarrega a página automaticamente uma única vez para
 * obter os novos hashes e evitar a "tela branca".
 */
function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg = (err as Error)?.message || String(err);
  const name = (err as Error)?.name || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

const RELOAD_KEY = '__lazy_chunk_reloaded__';

export default class LazyChunkBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log para debug
    console.error('[LazyChunkBoundary]', error, info);

    if (isChunkLoadError(error)) {
      // Evita loop infinito: só recarrega uma vez por sessão
      try {
        const already = sessionStorage.getItem(RELOAD_KEY);
        if (!already) {
          sessionStorage.setItem(RELOAD_KEY, '1');
          window.location.reload();
        }
      } catch {
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    try { sessionStorage.removeItem(RELOAD_KEY); } catch {}
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <div className="min-h-[60vh] flex items-center justify-center p-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        );
      }
      return (
        this.props.fallback ?? (
          <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 p-6 text-center">
            <h2 className="text-xl font-semibold">Algo deu errado ao abrir esta tela</h2>
            <p className="text-sm text-muted-foreground max-w-md">
              Tente recarregar a página. Se o problema persistir, entre em contato com o suporte.
            </p>
            <button
              onClick={this.handleReload}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
            >
              Recarregar página
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}